import React, { useEffect, useState } from 'react';
import { Lead, EligibilityStatus } from '../types';
import { calculateTimeRemaining, formatTimeRemaining } from '../utils/time';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { SendIcon, XCircleIcon, EyeIcon } from 'lucide-react';
interface LeadRowProps {
  lead: Lead;
  onViewDetails: (lead: Lead, eligibility: EligibilityStatus) => void;
  onAction: (leadId: string, action: 'send' | 'cancel') => void;
}
export function LeadRow({
  lead,
  onViewDetails,
  onAction
}: LeadRowProps) {
  const [timeRemaining, setTimeRemaining] = useState(() => calculateTimeRemaining(lead.timeEntered));
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(lead.timeEntered));
    }, 1000);
    return () => clearInterval(timer);
  }, [lead.timeEntered]);
  // Styling (only eligible leads appear here)
  const eligibility: EligibilityStatus = 'Eligible';
  let rowStyle = 'hover:bg-gray-50';
  let indicatorColor = 'bg-green-500';
  const timeColor = 'text-gray-900';
  if (timeRemaining === 0) {
    rowStyle = 'bg-gray-50/50';
    indicatorColor = 'bg-gray-400';
  }
  return <tr className={`group transition-colors border-b border-gray-100 last:border-0 ${rowStyle}`} data-id="element-87">
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-88">
        <div className="flex items-center" data-id="element-89">
          <div className={`w-2 h-2 rounded-full mr-3 ${indicatorColor}`} data-id="element-90" />
          <div data-id="element-91">
            <button onClick={() => onViewDetails(lead, eligibility)} className="text-sm font-medium text-gray-900 hover:text-indigo-600 transition-colors" data-id="element-92">
              {lead.name}
            </button>
            <div className="text-xs text-gray-500" data-id="element-93">{lead.phone}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-94">
        <span className="text-sm text-gray-600" data-id="element-95">{lead.source}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-96">
        <span className="text-sm text-gray-600" data-id="element-97">{lead.owner}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-98">
        <Badge variant={lead.status === 'New' || lead.status === 'Contact Attempted' ? 'success' : 'neutral'} data-id="element-99">
          {lead.status}
        </Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-100">
        <div className="flex flex-col" data-id="element-101">
          {timeRemaining > 0 ? <>
              <span className={`text-sm font-mono ${timeColor}`} data-id="element-102">
                {formatTimeRemaining(timeRemaining)}
              </span>
              <span className="text-xs text-gray-500 mt-0.5" data-id="element-103">Eligible</span>
            </> : <span className="text-xs text-gray-500 italic" data-id="element-104">Evaluating…</span>}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-105">
        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-106">
          <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50" onClick={() => onAction(lead.id, 'send')} title="Send Now" data-id="element-107">
            <SendIcon className="w-4 h-4" data-id="element-108" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-900 hover:bg-red-50" onClick={() => onAction(lead.id, 'cancel')} title="Cancel Send" data-id="element-109">
            <XCircleIcon className="w-4 h-4" data-id="element-110" />
          </Button>
          <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900" onClick={() => onViewDetails(lead, eligibility)} title="View Details" data-id="element-111">
            <EyeIcon className="w-4 h-4" data-id="element-112" />
          </Button>
        </div>
      </td>
    </tr>;
}